import { type FormEvent } from 'react';
import { CalendarBlank, Users, ShieldCheck, Tag } from '@phosphor-icons/react';
import type { BookingQuoteResponse } from '@pms/shared';
import { formatPrice } from '../../lib/format';

interface PromoProps {
  /** Live field value. */
  value: string;
  onChange: (v: string) => void;
  /** Apply the typed code (triggers a re-quote upstream). */
  onApply: (code: string) => void;
  /** Clear the applied code. */
  onClear: () => void;
  /** True while the quote (with the applied code) is refetching. */
  pending?: boolean;
}

/** Party composition label, e.g. "2 adults · 1 child". Children hidden when 0. */
export function partyLabel(adults: number, children: number): string {
  const a = `${adults} adult${adults === 1 ? '' : 's'}`;
  const c = children > 0 ? ` · ${children} child${children === 1 ? '' : 'ren'}` : '';
  return a + c;
}

/** Read-only stay + price breakdown from the server quote. */
export function StaySummary({
  quote,
  promo,
  party,
}: {
  quote: BookingQuoteResponse;
  promo?: PromoProps;
  /** When present, shows party composition (adults/children) instead of a raw guest count. */
  party?: { adults: number; children: number };
}) {
  const discount = quote.discount;
  const guestsLabel = party
    ? partyLabel(party.adults, party.children)
    : `${quote.guests} guest${quote.guests === 1 ? '' : 's'}`;
  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
      <h2 className="text-lg font-bold text-brand-navy">{quote.propertyName}</h2>
      <p className="text-sm text-gray-500">{quote.location} · {quote.unitName}</p>

      <dl className="mt-4 space-y-2 text-sm">
        <div className="flex items-center gap-2 text-gray-700">
          <CalendarBlank className="h-4 w-4 text-brand-blue" />
          {quote.checkIn} → {quote.checkOut} · {quote.nights} night{quote.nights === 1 ? '' : 's'}
        </div>
        <div className="flex items-center gap-2 text-gray-700">
          <Users className="h-4 w-4 text-brand-blue" /> {guestsLabel}
        </div>
      </dl>

      <div className="mt-4 border-t border-gray-100 pt-4">
        <ul className="space-y-1 text-sm text-gray-600">
          {quote.nightly.map((n) => (
            <li key={n.date} className="flex justify-between">
              <span>{n.date}</span>
              <span>{formatPrice(n.amount, quote.currency)}</span>
            </li>
          ))}
        </ul>
        {discount && (
          <div className="mt-2 flex items-center justify-between text-sm font-medium text-green-700">
            <span className="flex items-center gap-1.5">
              <Tag className="h-4 w-4" /> Applied: {discount.code}
            </span>
            <span>−{formatPrice(discount.amount, quote.currency)}</span>
          </div>
        )}
        <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
          <span className="font-semibold text-brand-navy">Total</span>
          <span className="text-xl font-extrabold text-brand-navy">{formatPrice(quote.total, quote.currency)}</span>
        </div>
      </div>

      {promo && <PromoField quote={quote} promo={promo} />}

      <p className="mt-4 flex items-center gap-1.5 rounded-lg bg-brand-blue-light px-3 py-2 text-xs font-medium text-brand-navy">
        <ShieldCheck className="h-4 w-4 text-brand-blue" />
        No charge today
        {quote.freeCancelUntil && (
          <span className="text-gray-500">· Free cancellation until {quote.freeCancelUntil.slice(0, 10)}</span>
        )}
      </p>
    </div>
  );
}

/** Optional promo-code entry below the price summary. Backward-compatible: empty ⇒ no-op. */
function PromoField({ quote, promo }: { quote: BookingQuoteResponse; promo: PromoProps }) {
  const applied = !!quote.discount;
  const miss = quote.promo && quote.promo.valid === false ? quote.promo.message : null;

  function apply(e: FormEvent) {
    e.preventDefault();
    const code = promo.value.trim();
    if (code) promo.onApply(code);
  }

  if (applied) {
    return (
      <div className="mt-4 flex items-center justify-between rounded-lg bg-green-50 px-3 py-2 text-xs font-medium text-green-700">
        <span className="flex items-center gap-1.5"><Tag className="h-4 w-4" /> Promo applied</span>
        <button type="button" onClick={promo.onClear} className="font-semibold underline hover:no-underline">
          Remove
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={apply} className="mt-4 border-t border-gray-100 pt-4">
      <label htmlFor="promoCode" className="text-xs font-semibold text-gray-500">Promo code</label>
      <div className="mt-1 flex gap-2">
        <input
          id="promoCode"
          name="promoCode"
          type="text"
          autoComplete="off"
          value={promo.value}
          onChange={(e) => promo.onChange(e.target.value)}
          placeholder="Have a code?"
          aria-invalid={!!miss}
          aria-describedby={miss ? 'promoCode-error' : undefined}
          className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm uppercase tracking-wide text-gray-900 outline-none placeholder:normal-case placeholder:tracking-normal focus-visible:ring-2 focus-visible:ring-brand-blue"
        />
        <button
          type="submit"
          disabled={!promo.value.trim() || promo.pending}
          className="shrink-0 rounded-lg border border-primary-200 px-4 py-2 text-sm font-semibold text-brand-navy hover:bg-brand-blue-light disabled:opacity-60"
        >
          {promo.pending ? 'Checking…' : 'Apply'}
        </button>
      </div>
      {miss && <p id="promoCode-error" role="alert" className="mt-1.5 text-xs font-medium text-red-600">{miss}</p>}
    </form>
  );
}
